import moment from "moment";
import { toast } from "react-toastify";
import { RepositoryFactory } from "../../Repository/RepositoryFactory";
import {
  API_ACCESS_ERROR,
  API_ACCESS_LOADING,
  API_ACCESS_SAVING,
  API_ACCESS_SET,
} from "../types";
import {
  safeErrorCode,
  safeErrorMessage,
  safeErrorPrevExpiresAt,
  stripSecrets,
} from "../../utils/apiAccessSafety";

/*
    API Access (static#49) — thunks.

    Two rules specific to this module, both about the IAM secret:

    1. `provision` and `rotate` hand their response to an `onReveal` callback and
       dispatch NOTHING but the re-fetched, secret-free GET shape. The secret
       lives only in the caller's component state.

    2. Nothing in here ever logs an error object. Axios attaches the whole HTTP
       response to its errors, and on these two endpoints that response body IS
       the secret — so `console.log("error: ", error)`, the pattern used
       elsewhere in this folder, would leak it to the browser console. Use
       `safeErrorMessage`, which reads string fields only.
*/

const ApiAccess = RepositoryFactory.get("apiAccess");

// Backend responses in this repo are commonly `{ success, data }` but not
// always; unwrap defensively (see the `data` vs `data.data` bugs in CLAUDE.md).
const unwrap = (response) => {
  const body = response?.data;
  if (body && typeof body === "object" && "data" in body) {
    return body.data;
  }
  return body;
};

const formatDate = (value) =>
  value && moment(value).isValid()
    ? moment(value).format("MMMM D, YYYY")
    : null;

export const fetchApiAccess =
  ({ silent = false } = {}) =>
  async (dispatch) => {
    if (!silent) {
      dispatch({ type: API_ACCESS_LOADING, payload: true });
    }
    try {
      const response = await ApiAccess.getApiAccess();
      dispatch({ type: API_ACCESS_SET, payload: unwrap(response) });
      return true;
    } catch (error) {
      const message = safeErrorMessage(
        error,
        "We couldn't load your API access details."
      );
      dispatch({ type: API_ACCESS_ERROR, payload: message });
      console.error("api-access: load failed —", message);
      return false;
    } finally {
      if (!silent) {
        dispatch({ type: API_ACCESS_LOADING, payload: false });
      }
    }
  };

/**
 * Create the customer's credentials.
 *
 * @param courseIds the courses to entitle the key to. The server caps the count
 *                  from the customer's plan and refuses course id 0, so this is
 *                  a request, not an instruction.
 * @param onReveal  called with { accessKeyId, secretAccessKey, apigwKeyValue }.
 *                  This is the ONLY place the secret goes. Hold it in local
 *                  component state; never put it in Redux.
 */
export const provisionApiAccess =
  (courseIds, onReveal = () => {}) =>
  async (dispatch) => {
    dispatch({ type: API_ACCESS_SAVING, payload: true });
    try {
      const response = await ApiAccess.provisionApiAccess(courseIds);
      const credentials = unwrap(response) || {};
      onReveal({
        accessKeyId: credentials.accessKeyId || null,
        secretAccessKey: credentials.secretAccessKey || null,
        apigwKeyValue: credentials.apigwKeyValue || null,
      });
      // Refresh the panel from the read endpoint, which never returns a
      // secret. Note we deliberately do NOT dispatch `credentials`.
      await dispatch(fetchApiAccess({ silent: true }));
      return true;
    } catch (error) {
      const message = safeErrorMessage(
        error,
        "We couldn't create your API credentials. Please try again."
      );
      dispatch({ type: API_ACCESS_ERROR, payload: message });
      toast.error(message);
      console.error("api-access: provision failed —", message);
      await dispatch(fetchApiAccess({ silent: true }));
      return false;
    } finally {
      dispatch({ type: API_ACCESS_SAVING, payload: false });
    }
  };

/**
 * Rotate the customer's credentials. The old key keeps working for 7 days.
 *
 * @param onReveal  same contract as provisionApiAccess.
 */
export const rotateApiAccess =
  (onReveal = () => {}) =>
  async (dispatch) => {
    dispatch({ type: API_ACCESS_SAVING, payload: true });
    try {
      const response = await ApiAccess.rotateApiAccess();
      const credentials = unwrap(response) || {};
      onReveal({
        accessKeyId: credentials.accessKeyId || null,
        secretAccessKey: credentials.secretAccessKey || null,
        apigwKeyValue: credentials.apigwKeyValue || null,
      });
      await dispatch(fetchApiAccess({ silent: true }));
      return true;
    } catch (error) {
      // AWS allows two access keys per IAM user, and a 7-day overlap uses
      // the whole budget — so a second rotation inside an open window is
      // an expected outcome, not a fault. Never surface the raw error.
      if (safeErrorCode(error) === "ROTATION_IN_PROGRESS") {
        const expires = formatDate(safeErrorPrevExpiresAt(error));
        const message = expires
          ? `A rotation is already in progress. Your previous key expires on ${expires}.`
          : "A rotation is already in progress. Your previous key is still active.";
        dispatch({ type: API_ACCESS_ERROR, payload: message });
        toast.info(message);
        await dispatch(fetchApiAccess({ silent: true }));
        return false;
      }
      const message = safeErrorMessage(
        error,
        "We couldn't rotate your API key. Please try again."
      );
      dispatch({ type: API_ACCESS_ERROR, payload: message });
      toast.error(message);
      console.error("api-access: rotate failed —", message);
      await dispatch(fetchApiAccess({ silent: true }));
      return false;
    } finally {
      dispatch({ type: API_ACCESS_SAVING, payload: false });
    }
  };

export const setApiCallLimit =
  (enabled, onSuccess = () => {}) =>
  async (dispatch) => {
    dispatch({ type: API_ACCESS_SAVING, payload: true });
    try {
      const response = await ApiAccess.setCallLimit(enabled);
      dispatch({
        type: API_ACCESS_SET,
        payload: stripSecrets(unwrap(response)),
      });
      toast.success(
        enabled
          ? "Call limit on. Calls beyond your plan limit will be rejected."
          : "Call limit off. Calls beyond your plan limit are billed at $0.005 each."
      );
      onSuccess();
      return true;
    } catch (error) {
      const message = safeErrorMessage(
        error,
        "We couldn't change your call limit. Please try again."
      );
      dispatch({ type: API_ACCESS_ERROR, payload: message });
      toast.error(message);
      console.error("api-access: call-limit update failed —", message);
      // Re-read so the toggle snaps back to the server's truth.
      await dispatch(fetchApiAccess({ silent: true }));
      return false;
    } finally {
      dispatch({ type: API_ACCESS_SAVING, payload: false });
    }
  };
